import { toast } from "sonner";
import { call, delay, put, takeLatest } from 'redux-saga/effects';
import {
  createUser as createUserService,
  getUserProfile as getUserProfileService,
  getUsersByFilter as getUsersByFilterService,
  getUsers as getUsersService,
  getUserById as getUserByIdService,
  loginUser as loginUserService,
  getMe as getMeService,
  updateUser as updateUserService,
  getPatientCard as getPatientCardService
} from '../../services/users';
import {
  setStoragePatient,
  setToken,
  setRefreshToken,
  setAccessExpiry,
  setUserData,
  getLoggedInUserId,
  getDefaultLandingPath
} from '../../utils/storageUtils';
import { logoutHelper } from '../../utils/logoutHelper';
import { isPatient } from '../../constants/roles';
import * as types from './types';
import { adaptResponseError } from "../../utils/utils";

/**
 * Message affiché quand le back refuse le couple identifiant / mot de
 * passe. Le back renvoie déjà une version i18n (clé
 * {@code error.auth.bad_credentials}) ; ce libellé n'est qu'un repli
 * pour les 401 sans enveloppe d'erreur exploitable.
 */
const BAD_CREDENTIALS_MESSAGE =
  "Identifiants incorrects. Vérifiez votre adresse e-mail et votre mot de passe.";

/** Repli générique quand le sign-in échoue pour une autre raison. */
const LOGIN_FAILED_MESSAGE =
  "La connexion a échoué. Veuillez réessayer.";

/**
 * Message à afficher pour un échec de {@code POST /users/sign-in}.
 *
 * <p>On lit {@code raw.message} et non {@code message} : ce dernier est
 * toujours renseigné par {@code apiRequest}, qui y place son propre repli
 * générique quand le back n'a pas renvoyé son enveloppe
 * {@code {status, error, message, timestamp}}. Seul {@code raw} permet de
 * savoir si le serveur s'est réellement exprimé.
 */
const resolveLoginErrorMessage = (loginRes) => {
  const serverMessage = loginRes?.raw?.message;
  if (serverMessage) return serverMessage;
  return loginRes?.status === 401
    ? BAD_CREDENTIALS_MESSAGE
    : LOGIN_FAILED_MESSAGE;
};

/**
 * Login flow post-refacto back :
 *   1. POST /users/sign-in → { accessToken, refreshToken, accessExpiry }
 *   2. Store tokens dans localStorage.
 *   3. GET /users/me → UserResponse polymorphe (discriminant `role`).
 *   4. Store userdata, rétro-compatible avec l'ancien shape
 *      ({user: ...} pour le code legacy qui lit userdata.user.id).
 */
function* loginUser({ payload }) {
  try {
    const loginRes = yield call(loginUserService, payload);

    if (!loginRes?.ok) {
      // `/users/sign-in` est appelé en `silent` : c'est ici, et nulle part
      // ailleurs, qu'on notifie l'échec (pas de doublon avec apiRequest).
      const message = resolveLoginErrorMessage(loginRes);
      toast.error(message, { id: message });
      yield put({ type: types.LOGIN_USER_FAILURE, payload: [] });
      return;
    }

    const { accessToken, refreshToken, accessExpiry } = loginRes.data || {};
    if (!accessToken) {
      toast.error("Réponse de connexion invalide.");
      yield put({ type: types.LOGIN_USER_FAILURE, payload: [] });
      return;
    }

    setToken(accessToken);
    if (refreshToken) setRefreshToken(refreshToken);
    if (accessExpiry) setAccessExpiry(accessExpiry);

    // Récupère le profil (le sign-in ne renvoie plus l'utilisateur).
    const meRes = yield call(getMeService);
    if (!meRes?.ok) {
      // Le toast est déjà émis par `apiRequest` (notifyApiError).
      yield put({ type: types.LOGIN_USER_FAILURE, payload: [] });
      return;
    }

    const user = meRes.data;
    setUserData(user);

    toast.success("Connexion réussie");
    yield put({ type: types.LOGIN_USER_SUCCESS, payload: user });
    // On laisse le toast Sonner (durée par défaut 2s) être visible avant
    // le hard reload qui détruit le portail Toaster — sans ce délai le
    // succès passait inaperçu (replace immédiat).
    yield delay(900);
    return window.location.replace(getDefaultLandingPath());
  } catch (error) {
    console.error('LoginSagaErr: ', error);
    toast.error(LOGIN_FAILED_MESSAGE, { id: LOGIN_FAILED_MESSAGE });
    yield put({ type: types.LOGIN_USER_FAILURE, payload: [] });
  }
}

function* getUserProfile() {
  try {
    const result = yield call(getUserProfileService);
    if (result?.ok) {
      yield put({ type: types.GET_USER_PROFILE_SUCCESS, payload: result.data });
    } else {
      // Le toast est déjà émis par `apiRequest` (notifyApiError).
      yield put({ type: types.GET_USER_PROFILE_FAILURE, payload: [] });
    }
  } catch (error) {
    console.error('GetUserProfileSagaErr: ', error);
    yield put({ type: types.GET_USER_PROFILE_FAILURE, payload: [] });
  }
}

/**
 * Phase 2.H — {@code GET /users} (listing massif) est interdit au runtime.
 * Le service {@code getUsersService} renvoie désormais un envelope 410 vide
 * sans HTTP. On garde le handler pour ne pas casser les watchers legacy
 * (et les anciens dispatchers s'il en restait), mais il ne toast plus
 * (échec silencieux) et alimente le store avec un payload vide.
 *
 * Tous les flux de rafraîchissement post-mutation doivent passer par des
 * actions role-aware (à introduire en Phase 4).
 */
function* getUsers() {
  try {
    const result = yield call(getUsersService);
    yield put({
      type: result?.ok ? types.GET_USERS_SUCCESS : types.GET_USERS_FAILURE,
      payload: result?.ok ? (result.data || []) : []
    });
  } catch (error) {
    console.error('GetUsersSagaErr: ', error);
    yield put({ type: types.GET_USERS_FAILURE, payload: [] });
  }
}

function* getUsersByFilter({ payload }) {
  try {
    const result = yield call(getUsersByFilterService, payload);
    if (result?.ok) {
      yield put({ type: types.GET_USERS_BY_FILTER_SUCCESS, payload: result.data });
    } else {
      // Le toast est déjà émis par `apiRequest` (notifyApiError).
      yield put({ type: types.GET_USERS_BY_FILTER_FAILURE, payload: [] });
    }
  } catch (error) {
    console.error('GetUsersByFilterSagaErr: ', error);
    yield put({ type: types.GET_USERS_BY_FILTER_FAILURE, payload: [] });
  }
}

/**
 * Crée un utilisateur puis enrichit le résultat.
 *
 * <p>Post-refacto, {@code POST /users/{role}} renvoie uniquement l'{@code id}
 * (Long). On ré-interroge {@code GET /users/{id}} pour récupérer le
 * {@code UserResponse} polymorphe complet, ce qui permet aux consommateurs
 * du store (Profile, ShowUser, Users) de continuer à lire l'objet utilisateur.
 *
 * <p>Si la création réussit mais que le refetch échoue, on n'invalide pas la
 * création (l'utilisateur existe en base) — on dispatch quand même
 * {@code CREATE_USER_SUCCESS} avec un payload minimal {@code {id}}.
 *
 * <p>Phase 2.H : on ne dispatche plus {@code GET_USERS_REQUEST} (listing
 * massif interdit). Les écrans consommateurs doivent rafraîchir leur propre
 * liste via les hooks role-aware (ex : {@code refetch} de la query
 * paginée patients dans {@code Patient/index.tsx} via {@code onSuccess}
 * de la mutation react-query).
 */
function* createUser({ payload }) {
  try {
    const result = yield call(createUserService, payload);
    if (!result?.ok) {
      // Le toast est déjà émis par `apiRequest` (notifyApiError).
      yield put({ type: types.CREATE_USER_FAILURE, payload: adaptResponseError(result) });
      return;
    }

    const newId = result.data;
    let createdUser = { id: newId };
    if (newId != null) {
      const userRes = yield call(getUserByIdService, newId);
      if (userRes?.ok) {
        createdUser = userRes.data;
      } else {
        console.warn(
          'CreateUserSaga: user créé (id=' + newId +
          ') mais refetch impossible — payload SUCCESS minimal.'
        );
      }
    }

    toast.success("Utilisateur créé avec succès");
    yield put({ type: types.CREATE_USER_SUCCESS, payload: createdUser });
    // Phase 2.H : plus de dispatch GET_USERS_REQUEST (listing massif interdit).
  } catch (error) {
    console.error("CreateUserSagaErr: ", error);
    yield put({ type: types.CREATE_USER_FAILURE, payload: [] });
  }
}

/**
 * Met à jour un utilisateur puis enrichit le résultat.
 * <p>Même contrat que {@link createUser} : le back renvoie un {@code Long}
 * (id, normalement identique au {@code payload.id}), on ré-interroge
 * {@code GET /users/{id}} pour obtenir l'objet à jour.
 *
 * <p>Si l'utilisateur mis à jour est un patient, on resynchronise
 * {@code storage.patient} (utilisé par le PDF carte patient). Le test
 * passe par le helper {@link isPatient} (discriminant {@code role}).
 */
function* updateUser({ payload }) {
  try {
    const result = yield call(updateUserService, payload);
    if (!result?.ok) {
      // Le toast est déjà émis par `apiRequest` (notifyApiError).
      yield put({ type: types.UPDATE_USER_FAILURE, payload: adaptResponseError(result) });
      return;
    }

    const updatedId = result.data ?? payload.id;
    let updatedUser = { id: updatedId };
    if (updatedId != null) {
      const userRes = yield call(getUserByIdService, updatedId);
      if (userRes?.ok) {
        updatedUser = userRes.data;
      } else {
        console.warn(
          'UpdateUserSaga: user mis à jour (id=' + updatedId +
          ') mais refetch impossible — payload SUCCESS minimal.'
        );
      }
    }

    if (isPatient(updatedUser)) {
      setStoragePatient(updatedUser);
    }

    // Self-update : on resync userdata + on rafraîchit le {@code profile}
    // dans le store via {@code GET_USER_PROFILE_SUCCESS}. Le reducer
    // {@code UPDATE_USER_SUCCESS} reste pur et ne lit plus localStorage.
    const loggedInUserId = getLoggedInUserId();
    if (loggedInUserId != null && updatedUser?.id === loggedInUserId) {
      setUserData(updatedUser);
      yield put({ type: types.GET_USER_PROFILE_SUCCESS, payload: updatedUser });
    }

    toast.success("Utilisateur mis à jour");
    yield put({ type: types.UPDATE_USER_SUCCESS, payload: updatedUser });
    // Phase 2.H : plus de dispatch GET_USERS_REQUEST (listing massif interdit).
  } catch (error) {
    console.error("UpdateUserSagaErr: ", error);
    yield put({ type: types.UPDATE_USER_FAILURE, payload: [] });
  }
}

/**
 * Logout : nettoie tous les tokens (access + refresh + expiry) et userdata,
 * puis redirige sur /login. Pas d'appel back tant qu'aucun endpoint logout
 * n'est exposé (cf. P2#7 — décision architecturale en attente).
 */
function* logoutUser() {
  yield call(logoutHelper);
  toast.success("Déconnexion réussie");
}

function* getPatientCard({ payload }) {
  yield call(getPatientCardService, payload);
}

export default function* AuthSaga() {
  yield takeLatest(types.LOGIN_USER_REQUEST, loginUser);
  yield takeLatest(types.GET_USERS_REQUEST, getUsers);
  yield takeLatest(types.GET_USER_PROFILE_REQUEST, getUserProfile);
  yield takeLatest(types.GET_USERS_BY_FILTER_REQUEST, getUsersByFilter);
  yield takeLatest(types.CREATE_USER_REQUEST, createUser);
  yield takeLatest(types.LOGOUT_USER_REQUEST, logoutUser);
  yield takeLatest(types.UPDATE_USER_REQUEST, updateUser);
  yield takeLatest(types.GET_PATIENT_CARD_REQUEST, getPatientCard);
}
